/* 2026-10 — Mobile Staff biometric lock gate.
 *
 * Rendered by AdminPage.jsx INSTEAD of the real sign-in form when:
 *   (a) the viewport is mobile/tablet width, AND
 *   (b) at least one admin has the mobile lock enabled, AND
 *   (c) this browser hasn't already passed the gate in the last 5 min.
 *
 * Passing the gate (biometric OR PIN) just unlocks the REAL sign-in
 * form below it — it never grants admin access by itself. Any ONE
 * enrolled admin's passkey/PIN is enough, since visitor identity isn't
 * known yet at this stage.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Fingerprint, ShieldCheck, Grid3x3 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';

export default function AdminMobileGate({ onPassed }) {
  const [webauthnBusy, setWebauthnBusy] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const supportsWebauthn = typeof window !== 'undefined' && browserSupportsWebAuthn();

  // Offer biometric immediately on mount if the browser supports it —
  // one tap less than making the admin press "Unlock" first.
  useEffect(() => { if (supportsWebauthn) tryWebauthn(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tryWebauthn = async () => {
    setWebauthnBusy(true);
    try {
      const options = await adminApi.mobileLockGateWebauthnOptions();
      const credential = await startAuthentication({ optionsJSON: options });
      await adminApi.mobileLockGateWebauthnVerify(credential);
      toast.success('Unlocked.');
      onPassed();
    } catch (e) {
      // User cancelled / no passkey on this device / verify failed —
      // fall back to the PIN tab silently, no scary error toast for
      // the common "I'll just use my PIN" case.
      setShowPin(true);
    } finally { setWebauthnBusy(false); }
  };

  const submitPin = async () => {
    if (pin.length < 4) { toast.error('Enter your PIN.'); return; }
    setPinBusy(true);
    try {
      await adminApi.mobileLockGatePin(pin);
      toast.success('Unlocked.');
      onPassed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Incorrect PIN.');
      setPin('');
    } finally { setPinBusy(false); }
  };

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-6" data-testid="admin-mobile-gate">
      <div className="w-full max-w-sm rounded-md border border-border bg-card p-6 shadow-panel">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-inset-top">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Mobile Lock</div>
            <div className="text-[15px] font-semibold">Unlock Admin Access</div>
          </div>
        </div>
        <p className="mt-4 text-[12px] text-muted-foreground">
          This phone requires an extra unlock step before showing the admin sign-in form.
        </p>

        {!showPin ? (
          <div className="mt-5 space-y-3">
            <Button
              onClick={tryWebauthn}
              disabled={webauthnBusy || !supportsWebauthn}
              data-testid="admin-mobile-gate-webauthn-btn"
              className="w-full gap-1.5"
            >
              <Fingerprint className="h-4 w-4" />
              {webauthnBusy ? 'Waiting for Face ID / Touch ID…' : 'Unlock with Face ID / Touch ID'}
            </Button>
            {!supportsWebauthn && (
              <p className="text-center text-[11px] text-muted-foreground">
                This browser doesn't support biometric unlock.
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowPin(true)}
              data-testid="admin-mobile-gate-use-pin"
              className="w-full text-center text-[11px] text-muted-foreground hover:text-primary hover:underline"
            >
              Use a PIN instead
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <Input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
              onKeyDown={(e) => e.key === 'Enter' && submitPin()}
              placeholder="Enter PIN"
              data-testid="admin-mobile-gate-pin-input"
              className="text-center text-[18px] tracking-[0.3em]"
            />
            <Button
              onClick={submitPin}
              disabled={pinBusy || pin.length < 4}
              data-testid="admin-mobile-gate-pin-submit"
              className="w-full gap-1.5"
            >
              <Grid3x3 className="h-3.5 w-3.5" /> {pinBusy ? 'Checking…' : 'Unlock'}
            </Button>
            {supportsWebauthn && (
              <button
                type="button"
                onClick={() => setShowPin(false)}
                data-testid="admin-mobile-gate-use-biometric"
                className="w-full text-center text-[11px] text-muted-foreground hover:text-primary hover:underline"
              >
                Use Face ID / Touch ID instead
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
